import React, { useEffect, useState } from 'react';
import { VehicleMotion } from '../../types/api';

interface AdViewerProps {
  ad?: {
    title: string;
    description: string;
    creativeType?: 'text' | 'image' | 'video';
    fundingStatus?: 'draft' | 'unfunded' | 'funded';
    videoUrl?: string;
    imageUrl?: string;
    motionData: VehicleMotion;
    categories?: string[];
    tags?: string[];
    views?: number;
    clicks?: number;
    analytics?: {
      demographics?: unknown;
      faces?: unknown[];
      objects?: unknown[];
    };
  };
}

const isYouTubeUrl = (url: string | undefined | null): boolean => {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();
    return host.includes('youtube.com') || host.includes('youtu.be');
  } catch {
    return false;
  }
};

const toYouTubeEmbedUrl = (url: string): string | null => {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();

    let videoId: string | null = null;

    if (host.includes('youtube.com')) {
      if (parsed.searchParams.has('v')) {
        videoId = parsed.searchParams.get('v');
      }
    } else if (host.includes('youtu.be')) {
      videoId = parsed.pathname.replace(/^\//, '') || null;
    }

    if (!videoId) return null;

    const base = `https://www.youtube.com/embed/${encodeURIComponent(videoId)}`;
    // Autoplay, loop, and mute to mimic the video tag behavior
    return `${base}?autoplay=1&loop=1&mute=1&playlist=${encodeURIComponent(videoId)}`;
  } catch {
    return null;
  }
};

const AdViewer: React.FC<AdViewerProps> = ({ ad }) => {
  const [videoError, setVideoError] = useState(false);

  useEffect(() => {
    setVideoError(false);
  }, [ad?.videoUrl, ad?.imageUrl, ad?.creativeType]);

  if (!ad) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 p-6 text-sm text-slate-500 dark:text-slate-400">
        <p className="font-medium text-slate-700 dark:text-slate-300">No ad selected</p>
        <p className="mt-1">Choose a creative from the feed to preview it here.</p>
      </div>
    );
  }

  const creativeType =
    ad.creativeType || (ad.videoUrl?.trim() ? 'video' : ad.imageUrl?.trim() ? 'image' : 'text');
  const videoSrc = ad.videoUrl?.trim() || '';
  const imageSrc = ad.imageUrl?.trim() || '';
  const fundingStatus = ad.fundingStatus || 'draft';
  const fundingStatusTone =
    fundingStatus === 'funded'
      ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100'
      : fundingStatus === 'unfunded'
        ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-100'
        : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 ring-1 ring-slate-200';

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 bg-black">
        {creativeType === 'video' && videoSrc && isYouTubeUrl(videoSrc) ? (
          <iframe
            key={videoSrc}
            src={toYouTubeEmbedUrl(videoSrc) ?? ''}
            title={ad.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="mx-auto aspect-video w-full bg-black"
          />
        ) : creativeType === 'video' && videoSrc && !videoError ? (
          <video
            key={videoSrc}
            src={videoSrc}
            autoPlay
            loop
            muted
            playsInline
            controls
            onError={() => setVideoError(true)}
            poster={ad.imageUrl}
            className="mx-auto max-h-[360px] w-full object-contain bg-black"
          />
        ) : creativeType === 'video' ? (
          imageSrc ? (
            <div className="space-y-2 bg-black p-3">
              <img
                src={imageSrc}
                alt={ad.title}
                className="mx-auto max-h-[360px] w-full object-contain bg-black"
              />
              <p className="text-center text-xs text-amber-300">
                This video could not be played in the preview. The uploaded media URL was kept, but
                playback failed in the browser.
              </p>
            </div>
          ) : (
            <div className="flex min-h-[240px] items-center justify-center bg-slate-950 px-6 py-10 text-center text-sm text-amber-200">
              This video could not be played in the preview.
            </div>
          )
        ) : creativeType === 'image' && imageSrc ? (
          <img
            src={imageSrc}
            alt={ad.title}
            className="mx-auto max-h-[360px] w-full object-contain bg-black"
          />
        ) : (
          <div className="flex min-h-[240px] flex-col items-center justify-center bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.35),_rgba(15,23,42,0.98)_42%,_rgba(2,6,23,1)_100%)] px-8 text-center text-slate-100">
            <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-cyan-200/80">
              {creativeType === 'image' ? 'Image creative unavailable' : 'Text creative'}
            </p>
            <h2 className="mt-4 max-w-4xl text-3xl font-semibold text-white">{ad.title}</h2>
            {ad.description && <p className="mt-4 max-w-3xl text-sm text-slate-300">{ad.description}</p>}
          </div>
        )}
      </div>

      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{ad.title}</h2>
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${fundingStatusTone}`}>
            {fundingStatus === 'funded'
              ? 'Funded'
              : fundingStatus === 'unfunded'
                ? 'Unfunded'
                : 'Draft'}
          </span>
        </div>
        <p className="text-sm text-slate-700 dark:text-slate-300">{ad.description}</p>
        {(ad.categories || ad.tags) && (
          <div className="mt-2 space-y-1 text-xs text-slate-600 dark:text-slate-400">
            {ad.categories && ad.categories.length > 0 && (
              <p>
                <span className="font-semibold">Categories:</span> {ad.categories.join(', ')}
              </p>
            )}
            {ad.tags && ad.tags.length > 0 && (
              <p>
                <span className="font-semibold">Tags:</span> {ad.tags.join(', ')}
              </p>
            )}
            {(typeof ad.views === 'number' || typeof ad.clicks === 'number') && (
              <p>
                <span className="font-semibold">Engagement:</span>{' '}
                {typeof ad.views === 'number' ? `${ad.views} view${ad.views === 1 ? '' : 's'}` : ''}
                {typeof ad.clicks === 'number'
                  ? ` • ${ad.clicks} click${ad.clicks === 1 ? '' : 's'}`
                  : ''}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Vehicle motion data removed for customer-selected ads */}
    </div>
  );
};

export default AdViewer;
